import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, type Ref } from 'react';
import { sanitizeToNumeric } from 'vopay';
import { Title } from 'UI';
import { useTranslation } from 'react-i18next';
import ApprovalTierRow, { type ApprovalTierRowRef } from './ApprovalTierRow';
import styles from './ApprovalTiers.module.scss';
import DetailsCard from '@/components/UI/DetailsCard';

export type ApprovalTier = {
  workflowId?: string; // set when loaded from API; absent for newly added tiers
  startAmount: string; // editable for tier 1; auto-filled from prev end for tier 2+
  endAmount: string;
  noUpperLimit: boolean;
  approversNeeded: string;
};

export const EMPTY_TIER: ApprovalTier = {
  startAmount: '',
  endAmount: '',
  noUpperLimit: false,
  approversNeeded: '',
};

export type ApprovalTiersRef = { touchAll: () => void };

type ApprovalTiersProps = {
  value: ApprovalTier[];
  onChange: (tiers: ApprovalTier[]) => void;
  onValidityChange?: (allValid: boolean) => void;
  onFocus?: () => void;
  disabled?: boolean;
  currency?: string;
  ref?: Ref<ApprovalTiersRef>;
};

function parseAmount(amount: string): number {
  const sanitized = sanitizeToNumeric(amount);
  return parseFloat(sanitized || '0') || 0;
}

function ApprovalTiers({
  value,
  onChange,
  onValidityChange,
  onFocus,
  disabled = false,
  currency = 'CAD',
  ref,
}: ApprovalTiersProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'TransactionApproval.Configuration.Workflows.Update' });
  const rowRefs = useRef<(ApprovalTierRowRef | null)[]>([]);
  const [rowValidity, setRowValidity] = useState<Record<number, boolean>>({});

  // Clean up stale refs and validity entries when tiers are removed
  rowRefs.current = rowRefs.current.slice(0, value.length);

  useEffect(() => {
    setRowValidity((prev) => {
      const hasStale = Object.keys(prev).some((k) => Number(k) >= value.length);
      if (!hasStale) return prev;
      const next: Record<number, boolean> = {};
      for (let i = 0; i < value.length; i++) {
        if (prev[i] !== undefined) next[i] = prev[i];
      }
      return next;
    });
  }, [value.length]);

  useEffect(() => {
    const allValid = value.length > 0 && value.every((_, i) => rowValidity[i] === true);
    onValidityChange?.(allValid);
  }, [rowValidity, value.length]);

  useImperativeHandle(ref, () => ({
    touchAll: () => rowRefs.current.forEach((r) => r?.touchAll()),
  }));

  const handleRowValidityChange = useCallback((index: number, isValid: boolean) => {
    setRowValidity((prev) => (prev[index] === isValid ? prev : { ...prev, [index]: isValid }));
  }, []);

  const handleRowFocus = useCallback((index: number) => {
    for (let i = 0; i < index; i++) {
      rowRefs.current[i]?.touchAll();
    }
  }, []);

  const crossTierErrors = useMemo(() => {
    const errors: Record<number, string> = {};

    for (let i = 0; i < value.length; i++) {
      const tier = value[i];
      const next = value[i + 1];

      if (!tier.endAmount || tier.noUpperLimit) continue;

      if (next && next.endAmount && !next.noUpperLimit) {
        const tierEnd = parseAmount(tier.endAmount);
        const nextEnd = parseAmount(next.endAmount);
        if (tierEnd >= nextEnd) {
          errors[i] = t('ApprovalTiers.Validation.cannotExceed', {
            tierNum: i + 2,
            defaultValue: `This cannot exceed Tier ${i + 2} upper bound.`,
          });
        }
      }
    }

    return errors;
  }, [value, t]);

  const handleTierChange = useCallback(
    (index: number, updatedTier: ApprovalTier) => {
      let newTiers = [...value];
      const prevTier = newTiers[index];
      newTiers[index] = updatedTier;

      if (updatedTier.endAmount !== prevTier.endAmount && index + 1 < newTiers.length) {
        newTiers[index + 1] = { ...newTiers[index + 1], startAmount: updatedTier.endAmount };
      }

      if (updatedTier.noUpperLimit && !prevTier.noUpperLimit) {
        newTiers = newTiers.slice(0, index + 1);
      }

      const lastTier = newTiers[newTiers.length - 1];
      if (lastTier && lastTier.endAmount && !lastTier.noUpperLimit) {
        newTiers.push({ ...EMPTY_TIER, startAmount: lastTier.endAmount });
      }

      onChange(newTiers);
    },
    [value, onChange],
  );

  return (
    <DetailsCard style={{ padding: 0 }}>
      <div className={styles.container}>
        <div className={styles.header}>
          <Title level="5">{t('ApprovalTiers.title', { defaultValue: 'Approval Tiers' })}</Title>
          <p className={styles.description}>
            {t('ApprovalTiers.description', {
              defaultValue:
                'Set approval requirements based on transaction amount ranges. This applies to all selected payment rails.',
            })}
          </p>
        </div>
        <div className={styles.tableContainer}>
          <div className={styles.tableHeader}>
            <div className={styles.headerCell}>{t('ApprovalTiers.Header.tier', { defaultValue: 'Tier' })}</div>
            <div className={styles.headerCell}>
              {t('ApprovalTiers.Header.amountRange', { defaultValue: 'Amount Range' })}
            </div>
            <div className={styles.headerCell}>
              {t('ApprovalTiers.Header.approversNeeded', { defaultValue: 'Approvers Needed' })}
            </div>
          </div>

          <div className={styles.rows}>
            {value.map((tier, index) => (
              <ApprovalTierRow
                key={index}
                ref={(el) => {
                  rowRefs.current[index] = el;
                }}
                index={index}
                tier={tier}
                isFirstTier={index === 0}
                currency={currency}
                disabled={disabled}
                onChange={handleTierChange}
                onValidityChange={handleRowValidityChange}
                onFocus={() => {
                  handleRowFocus(index);
                  onFocus?.();
                }}
                crossTierError={crossTierErrors[index]}
              />
            ))}
          </div>
        </div>
      </div>
    </DetailsCard>
  );
}

export default ApprovalTiers;
